import { useCallback, useMemo, useState } from 'react';

import { ExtendedForecastListItem } from '@futrli/views/processors/forecasts/forecastsList';
import { readPersistedFilters, useListFilterPersistence } from '@futrli/predict/utils/list-filters';

import { ForecastTypeFilterValues } from '../types';

const STORAGE_KEY = 'futrli:forecast-dashboard-filters';

interface ForecastFilters {
  statusFilterValue: string;
  typeShownFilterValue: ForecastTypeFilterValues;
  sortByFilterValue: string;
}

const getValidStatusFilter = (statusFilterValue: string, isResourceSharingEnabled: boolean): string => {
  const validValues = isResourceSharingEnabled ? ['all', 'shared', 'private'] : ['all', 'published', 'draft'];
  return validValues.includes(statusFilterValue) ? statusFilterValue : 'all';
};

const matchesSearch = (forecast: ExtendedForecastListItem, searchTerm: string): boolean => {
  if (!searchTerm) {
    return true;
  }
  return forecast.name.toLowerCase().includes(searchTerm.toLowerCase());
};

const matchesStatus = (
  forecast: ExtendedForecastListItem,
  statusFilterValue: string,
  isResourceSharingEnabled: boolean
): boolean => {
  if (statusFilterValue === 'all') {
    return true;
  }

  if (isResourceSharingEnabled) {
    return forecast.permission_info.sharing_status === statusFilterValue;
  }

  return forecast.status === statusFilterValue;
};

const matchesTypeShown = (
  forecast: ExtendedForecastListItem,
  typeShownFilterValue: ForecastTypeFilterValues
): boolean => {
  switch (typeShownFilterValue) {
    case ForecastTypeFilterValues.ALL:
      return true;
    case ForecastTypeFilterValues.THREE_WAY_FORECASTS:
      return forecast.kind === 'scenario';
    case ForecastTypeFilterValues.P_AND_L_BUDGETS:
      return forecast.kind === 'budget';
    default:
      return true;
  }
};

const sortForecasts = (
  forecasts: ExtendedForecastListItem[],
  sortByFilterValue: string
): ExtendedForecastListItem[] => {
  const sorted = [...forecasts];

  switch (sortByFilterValue) {
    case 'date-created-newest':
      return sorted.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
    case 'date-created-oldest':
      return sorted.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
    case 'name-a-z':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'name-z-a':
      return sorted.sort((a, b) => b.name.localeCompare(a.name));
    case 'start-date-oldest':
      return sorted.sort((a, b) => {
        if (!a.forecast_from_date) {
          return 1;
        }
        if (!b.forecast_from_date) {
          return -1;
        }
        return new Date(a.forecast_from_date).getTime() - new Date(b.forecast_from_date).getTime();
      });
    case 'start-date-newest':
      return sorted.sort((a, b) => {
        if (!a.forecast_from_date) {
          return 1;
        }
        if (!b.forecast_from_date) {
          return -1;
        }
        return new Date(b.forecast_from_date).getTime() - new Date(a.forecast_from_date).getTime();
      });
    default:
      return sorted;
  }
};

export const useMyForecastsFilters = (
  forecasts: ExtendedForecastListItem[],
  organisationId: string,
  isResourceSharingEnabled = false
) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilterValue, setStatusFilterValue] = useState(() => {
    const persisted = readPersistedFilters(STORAGE_KEY, organisationId);
    return getValidStatusFilter(
      (persisted?.filters?.statusFilterValue as string) ?? 'all',
      isResourceSharingEnabled
    );
  });
  const [typeShownFilterValue, setTypeShownFilterValue] = useState(() => {
    const persisted = readPersistedFilters(STORAGE_KEY, organisationId);
    return (persisted?.filters?.typeShownFilterValue as ForecastTypeFilterValues) ?? 'all';
  });
  const [sortByFilterValue, setSortByFilterValue] = useState(() => {
    const persisted = readPersistedFilters(STORAGE_KEY, organisationId);
    return (persisted?.filters?.sortByFilterValue as string) ?? 'date-created-newest';
  });

  const onSearchChange = useCallback((value: string) => setSearchTerm(value), []);
  const onStatusFilterChange = useCallback((value: string) => setStatusFilterValue(value), []);
  const onTypeShownFilterChange = useCallback(
    (value: string) => setTypeShownFilterValue(value as ForecastTypeFilterValues),
    []
  );
  const onSortByFilterChange = useCallback((value: string) => setSortByFilterValue(value), []);

  const filters = useMemo<ForecastFilters>(() => ({ statusFilterValue, typeShownFilterValue, sortByFilterValue }), [
    statusFilterValue,
    typeShownFilterValue,
    sortByFilterValue
  ]);

  const handleRestore = useCallback(
    (restoredFilters: ForecastFilters) => {
      setStatusFilterValue(getValidStatusFilter(restoredFilters.statusFilterValue, isResourceSharingEnabled));
      setTypeShownFilterValue(restoredFilters.typeShownFilterValue);
      setSortByFilterValue(restoredFilters.sortByFilterValue);
    },
    [isResourceSharingEnabled]
  );

  useListFilterPersistence({
    storageKey: STORAGE_KEY,
    organisationId,
    filters,
    sort: undefined,
    onRestore: handleRestore
  });

  const filteredForecasts = useMemo(() => {
    const filtered = forecasts.filter(
      forecast =>
        matchesSearch(forecast, searchTerm) &&
        matchesStatus(forecast, statusFilterValue, isResourceSharingEnabled) &&
        matchesTypeShown(forecast, typeShownFilterValue)
    );

    return sortForecasts(filtered, sortByFilterValue);
  }, [forecasts, searchTerm, statusFilterValue, typeShownFilterValue, sortByFilterValue, isResourceSharingEnabled]);

  return {
    searchTerm,
    onSearchChange,
    statusFilterValue,
    onStatusFilterChange,
    typeShownFilterValue,
    onTypeShownFilterChange,
    sortByFilterValue,
    onSortByFilterChange,
    filteredForecasts
  };
};
